import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Navbar } from "./navbar";
import { ArrowLeft, Lock, Package } from "lucide-react";
import { addCommande, deleteCartItem, getDataCommandes } from "./action";
import "../styles/paiement.css";

export function Paiement() {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // We expect { method, total, orderInfo } to be passed via route state
    const { method, total, orderInfo, items } = location.state || {};
    const { commandes } = useSelector(state => state);

    const [cardInfo, setCardInfo] = useState({
        number: "",
        name: "",
        expiry: "",
        cvc: ""
    });

    // Redirect if no method selected (e.g., direct navigation)
    useEffect(() => {
        if (!method) {
            navigate("/valider");
        }
        dispatch(getDataCommandes());
    }, [method, navigate, dispatch]);

    if (!method) return null;

    const handleCardChange = (e) => {
        setCardInfo({ ...cardInfo, [e.target.name]: e.target.value });
    };

    const handlePaymentSubmit = async (e) => {
        e.preventDefault();

        // 1. Prepare New Order
        const nextId = (commandes.length + 1).toString();
        const newOrder = {
            id: nextId,
            username_Client: orderInfo?.nom || "Client",
            total: total,
            date: new Date().toISOString().split('T')[0],
            statut: "en attente",
            method: method,
            adresse: orderInfo?.adresse,
            ville: orderInfo?.ville,
            telephone: orderInfo?.telephone,
            produits: items.map(item => ({
                id: item.originalId || item.id,
                nom: item.nomClimatisseur,
                quantite: item.quantite,
                prix: item.prix,
                taille: item.taille,
                color: item.color
            }))
        };

        // 2. Save Order to data.json
        try {
            await dispatch(addCommande(newOrder)).unwrap();

            // 3. Clear Cart (delete each item on server)
            const deletePromises = items.map(item => dispatch(deleteCartItem(item.id)).unwrap());
            await Promise.all(deletePromises);

            alert(`Paiement de ${total?.toLocaleString() || 0} DH réussi via ${method.toUpperCase()} ! \nMerci pour votre commande.`);
            navigate("/");
            
        } catch (err) {
            console.error("Erreur lors de la validation de la commande:", err);
            alert("Une erreur est survenue lors de la validation. Veuillez réessayer.");
        }
    };

    return (
        <div className="payment-gateway-page">
            <Navbar />

            <div className="payment-gateway-container">
                <div className="payment-card-wrapper">

                    <div className="payment-header">
                        <button className="back-btn-payment" onClick={() => navigate(-1)}>
                            <span>←</span> Retour
                        </button>
                        <h2>Paiement Sécurisé</h2>
                        <div className="payment-badges">
                            <span><Lock size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '5px' }} /> 100% Sécurisé</span>
                        </div>
                    </div>

                    <div className="payment-amount-box">
                        <span>Montant à régler</span>
                        <h2>{total?.toLocaleString()} DH</h2>
                        {orderInfo?.nom && <p>Commande pour: {orderInfo.nom}</p>}
                    </div>

                    {method === "visa" && (
                        <form className="card-payment-form" onSubmit={handlePaymentSubmit}>
                            <div className="card-logos">
                                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/2560px-Visa_Inc._logo.svg.png" alt="Visa" />
                                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo.svg/1280px-Mastercard-logo.svg.png" alt="MasterCard" />
                            </div>

                            <div className="pg-input-group">
                                <label>Numéro de carte</label>
                                <input
                                    type="text"
                                    name="number"
                                    placeholder="0000 0000 0000 0000"
                                    maxLength="19"
                                    value={cardInfo.number}
                                    onChange={handleCardChange}
                                    required
                                />
                            </div>

                            <div className="pg-input-group">
                                <label>Nom sur la carte</label>
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="Nom complet"
                                    value={cardInfo.name}
                                    onChange={handleCardChange}
                                    required
                                />
                            </div>

                            <div className="card-row">
                                <div className="pg-input-group">
                                    <label>Date d'expiration</label>
                                    <input
                                        type="text"
                                        name="expiry"
                                        placeholder="MM/AA"
                                        maxLength="5"
                                        value={cardInfo.expiry}
                                        onChange={handleCardChange}
                                        required
                                    />
                                </div>
                                <div className="pg-input-group">
                                    <label>CVC / CVV</label>
                                    <input
                                        type="text"
                                        name="cvc"
                                        placeholder="123"
                                        maxLength="4"
                                        value={cardInfo.cvc}
                                        onChange={handleCardChange}
                                        required
                                    />
                                </div>
                            </div>

                            <button type="submit" className="btn-pay-now">
                                Confirmer et Payer {total?.toLocaleString()} DH
                            </button>
                        </form>
                    )}

                    {method === "paypal" && (
                        <div className="paypal-payment-container">
                            <img src="https://upload.wikimedia.org/wikipedia/commons/b/b5/PayPal.svg" alt="PayPal" className="paypal-logo-big" />
                            <p className="paypal-desc">Vous allez être redirigé vers l'interface sécurisée de PayPal pour finaliser votre transaction.</p>

                            <button onClick={handlePaymentSubmit} className="btn-paypal-login">
                                Se connecter à PayPal
                            </button>
                            <p className="paypal-terms">La transaction est sécurisée et protégée par PayPal.</p>
                        </div>
                    )}

                    {method === "livraison" && (
                        <div className="cod-payment-container">
                            <div className="cod-icon-big"><Package size={80} color="#000033" /></div>
                            <h3>Paiement à la livraison</h3>
                            <p className="cod-desc">
                                Votre commande sera validée immédiatement.
                                Vous paierez <strong style={{ color: '#700000' }}>{total?.toLocaleString()} DH</strong> en espèces directement au livreur lors de la réception de vos articles à l'adresse suivante :
                            </p>
                            <div className="address-review-box">
                                {orderInfo?.adresse}<br />
                                {orderInfo?.ville}<br />
                                {orderInfo?.telephone}
                            </div>

                            <button onClick={handlePaymentSubmit} className="btn-cod-confirm">
                                Confirmer ma commande
                            </button>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}
